/**
 * collection-brand コレクションの「もっと見る」
 * 1〜10件目の特殊レイアウトは残し、11件目以降を通常グリッドへ追加する
 */

const collectProductIds = (root: HTMLElement) => {
  const ids = new Set<string>();
  const items = root.querySelectorAll<HTMLElement>(
    "[data-collection-brand-item], [data-collection-brand-rest] > [data-product-id]",
  );

  for (const item of items) {
    const productId = item.dataset.productId || "";
    if (productId !== "") {
      ids.add(productId);
    }
  }

  return ids;
};

const toSectionUrl = (urlValue: string, sectionId: string) => {
  const url = new URL(urlValue, window.location.origin);
  url.searchParams.set("section_id", sectionId);
  url.hash = "";
  return url.toString();
};

const toFallbackHref = (urlValue: string, anchorId: string) => {
  const url = new URL(urlValue, window.location.origin);
  url.searchParams.delete("section_id");
  const query = url.search;
  return `${url.pathname}${query}#${anchorId}`;
};

const FADE_SELECTOR =
  "[data-collection-brand-item], [data-collection-brand-rest] > [data-product-id]";

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

// 修正: 画面上側80%に入ったらフェードインし、外れたら戻す
const fadeObserver = prefersReducedMotion
  ? null
  : new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle("is-inview", entry.isIntersecting);
        }
      },
      {
        rootMargin: "0px 0px -20% 0px",
        threshold: 0,
      },
    );

const observeFade = (elements: Iterable<Element>): void => {
  for (const element of elements) {
    if (!(element instanceof HTMLElement)) continue;
    if (element.dataset.collectionBrandFade === "true") continue;

    element.dataset.collectionBrandFade = "true";

    if (!fadeObserver) {
      element.classList.add("is-inview");
      continue;
    }

    element.classList.add("is-fade");
    fadeObserver.observe(element);
  }
};

const getRestItems = (source: HTMLElement) => {
  return [...source.children].filter((child): child is HTMLElement => {
    return (
      child instanceof HTMLElement && child.hasAttribute("data-product-id")
    );
  });
};

const initLoadMore = (root: HTMLElement): void => {
  const sectionId = root.dataset.sectionId || "";
  const moreLink = root.querySelector<HTMLAnchorElement>(
    "[data-collection-brand-more]",
  );
  const restWrap = root.querySelector<HTMLElement>(
    "[data-collection-brand-rest-wrap]",
  );
  const restList = root.querySelector<HTMLElement>("[data-collection-brand-rest]");
  const status = root.querySelector<HTMLElement>("[data-collection-brand-status]");
  let nextUrl = root.dataset.nextUrl || "";

  if (
    !sectionId ||
    !moreLink ||
    !restWrap ||
    !restList ||
    !status ||
    nextUrl === ""
  ) {
    return;
  }

  const label = moreLink.dataset.label || "もっと見る";
  const loadingLabel = moreLink.dataset.loadingLabel || "読み込み中";
  let isLoading = false;
  let lastPage = 1;

  const setLoading = (loading: boolean) => {
    isLoading = loading;
    moreLink.setAttribute("aria-disabled", loading ? "true" : "false");
    moreLink.setAttribute("aria-busy", loading ? "true" : "false");
    restWrap.setAttribute("aria-busy", loading ? "true" : "false");
    moreLink.textContent = loading ? loadingLabel : label;
  };

  const loadMore = async (): Promise<void> => {
    if (isLoading || nextUrl === "") return;

    setLoading(true);
    status.classList.remove("is-error");
    status.textContent = "商品を読み込んでいます";

    try {
      const response = await fetch(toSectionUrl(nextUrl, sectionId), {
        headers: { Accept: "text/html" },
      });

      if (!response.ok) {
        throw new Error(`status ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const pageRoot = doc.querySelector<HTMLElement>("[data-collection-brand-page]");
      const source = pageRoot?.querySelector<HTMLElement>(
        "[data-collection-brand-rest-source]",
      );

      if (!pageRoot || !source) {
        throw new Error("missing collection brand page");
      }

      const currentPage = Number(pageRoot.dataset.currentPage || "0");
      if (!Number.isFinite(currentPage) || currentPage <= lastPage) {
        throw new Error("unexpected page");
      }

      const existingIds = collectProductIds(root);
      const items = getRestItems(source);
      const addedItems: HTMLElement[] = [];

      for (const item of items) {
        const productId = item.dataset.productId || "";
        if (productId === "" || existingIds.has(productId)) continue;

        existingIds.add(productId);
        restList.appendChild(item);
        addedItems.push(item);
      }

      const added = addedItems.length;

      if (added === 0) {
        throw new Error("no products");
      }

      lastPage = currentPage;
      nextUrl = pageRoot.dataset.nextUrl || "";
      if (nextUrl === "") {
        delete root.dataset.nextUrl;
      } else {
        root.dataset.nextUrl = nextUrl;
        moreLink.href = toFallbackHref(nextUrl, root.id);
      }

      restWrap.hidden = false;
      observeFade(addedItems);
      moreLink.setAttribute("aria-expanded", "true");
      status.classList.remove("is-error");
      status.textContent =
        nextUrl === "" ? "すべての商品を表示しました" : "商品を追加しました";

      if (nextUrl === "") {
        if (document.activeElement === moreLink) {
          status.tabIndex = -1;
          status.focus();
        }
        moreLink.hidden = true;
      }
    } catch {
      status.classList.add("is-error");
      status.textContent =
        "商品を読み込めませんでした。もう一度お試しください。";
    } finally {
      if (!moreLink.hidden) {
        setLoading(false);
      } else {
        isLoading = false;
        restWrap.setAttribute("aria-busy", "false");
      }
    }
  };

  moreLink.addEventListener("click", (event: MouseEvent) => {
    event.preventDefault();
    void loadMore();
  });
};

export const collectionBrand = (): void => {
  const roots = document.querySelectorAll<HTMLElement>(
    "[data-collection-brand]",
  );

  for (const root of roots) {
    observeFade(root.querySelectorAll(FADE_SELECTOR));
    initLoadMore(root);
  }
};
